import { z } from 'zod';

export const FOLLOW_UP_TYPES = ['call', 'email', 'meeting', 'demo', 'whatsapp', 'other'] as const;
export const followUpTypeEnum = z.enum(FOLLOW_UP_TYPES);
export type FollowUpType = z.infer<typeof followUpTypeEnum>;

export const FOLLOW_UP_PRIORITIES = ['low', 'medium', 'high'] as const;
export const followUpPriorityEnum = z.enum(FOLLOW_UP_PRIORITIES);
export type FollowUpPriority = z.infer<typeof followUpPriorityEnum>;

export const FOLLOW_UP_STATUSES = ['pending', 'completed', 'overdue', 'cancelled'] as const;
export const followUpStatusEnum = z.enum(FOLLOW_UP_STATUSES);
export type FollowUpStatus = z.infer<typeof followUpStatusEnum>;

/**
 * A scheduled reminder against a lead/customer/opportunity — deferred from
 * the CRM module's core pass, ported now from Office_project's `FollowUp`
 * model. `status` starts `pending` and transitions server-side (a
 * scheduler flips overdue ones, `complete()` flips completed) — never
 * client-set on create, mirroring the source.
 */
export const createFollowUpSchema = z.object({
  type: followUpTypeEnum.default('call'),
  title: z.string().trim().min(2, 'Title must be at least 2 characters').max(200),
  notes: z.string().trim().max(2000).optional().or(z.literal('')),
  dueDate: z.coerce.date(),
  priority: followUpPriorityEnum.default('medium'),

  leadId: z.string().optional(),
  customerId: z.string().optional(),
  opportunityId: z.string().optional(),

  assignedTo: z.string().optional(),
});

export type CreateFollowUpInput = z.infer<typeof createFollowUpSchema>;

export const updateFollowUpSchema = createFollowUpSchema.partial();
export type UpdateFollowUpInput = z.infer<typeof updateFollowUpSchema>;

export const completeFollowUpSchema = z.object({
  completionNote: z.string().trim().max(1000).optional().or(z.literal('')),
});

export type CompleteFollowUpInput = z.infer<typeof completeFollowUpSchema>;

export const followUpListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: followUpStatusEnum.optional(),
  assignedTo: z.string().optional(),
  leadId: z.string().optional(),
  customerId: z.string().optional(),
  overdue: z.coerce.boolean().optional(),
});

export type FollowUpListQuery = z.infer<typeof followUpListQuerySchema>;
